/**
 * 模型目录 API（Phase 42-12）
 *
 * 对接后端 /model-config/catalog 端点（Phase 42-04 完成）
 */

import apiClient from './index'

// ==================== 类型定义 ====================

export type CatalogCurrency = 'CNY' | 'USD'
export type CatalogModelType = 'llm' | 'embedding' | 'rerank' | 'multimodal' | 'image' | 'video'
/** 生命周期：正常服务 / 计划下线 / 已弃用 / 已下线（deprecated、retired 时 is_deprecated 为真） */
export type CatalogLifecycleStatus = 'active' | 'scheduled' | 'deprecated' | 'retired'

/** 模型目录条目（对应后端 ModelCatalog ORM） */
export interface CatalogModel {
  model_id: string
  provider: string
  display_name: string
  model_type?: CatalogModelType
  embedding_dimension?: number | null
  supported_dimensions?: number[] | null
  supports_tools: boolean
  supports_vision: boolean
  supports_image_gen: boolean
  supports_video_gen: boolean
  cost_per_1k_prompt: number
  cost_per_1k_completion: number
  cost_per_image: number
  currency: CatalogCurrency
  context_window: number
  max_output_tokens: number | null
  downgrade_to: string | null
  tags: string[]
  is_enabled: boolean
  is_deprecated?: boolean
  lifecycle_status?: CatalogLifecycleStatus
  /** 供应商公告的下线时间（ISO） */
  retire_at?: string | null
  /** 下线依据（公告编号或链接） */
  retire_notice?: string | null
  /** 替代模型 model_id */
  replacement_model?: string | null
  created_at?: string
  updated_at?: string
}

/** 获取模型目录的查询参数 */
export interface FetchModelCatalogParams {
  provider?: string
  model_type?: CatalogModelType
  tag?: string
  /**
   * 只返回"运行时可用"的模型。true 时后端 JOIN provider configs 过滤：
   * - catalog 条目启用
   * - provider 条目启用
   * - provider 已配置非空 api_key
   * 运行前校验、可用性面板传 true；工作流节点下拉框传 false，
   * 允许先选择未配置 API Key 的目录模型，运行时再校验凭证。
   */
  available_only?: boolean
}

/** 新增模型目录条目请求 */
export interface CreateCatalogModelRequest {
  model_id: string
  provider: string
  display_name: string
  model_type?: CatalogModelType
  embedding_dimension?: number | null
  supported_dimensions?: number[] | null
  supports_tools?: boolean
  supports_vision?: boolean
  supports_image_gen?: boolean
  supports_video_gen?: boolean
  cost_per_1k_prompt?: number
  cost_per_1k_completion?: number
  cost_per_image?: number
  currency?: CatalogCurrency
  context_window?: number
  max_output_tokens?: number | null
  downgrade_to?: string | null
  tags?: string[]
}

/** 更新模型目录条目请求 */
export interface UpdateCatalogModelRequest {
  display_name?: string
  model_type?: CatalogModelType
  embedding_dimension?: number | null
  supported_dimensions?: number[] | null
  supports_tools?: boolean
  supports_vision?: boolean
  supports_image_gen?: boolean
  supports_video_gen?: boolean
  cost_per_1k_prompt?: number
  cost_per_1k_completion?: number
  cost_per_image?: number
  currency?: CatalogCurrency
  context_window?: number
  max_output_tokens?: number | null
  downgrade_to?: string | null
  tags?: string[]
  lifecycle_status?: CatalogLifecycleStatus
  retire_at?: string | null
  retire_notice?: string | null
  replacement_model?: string | null
}

/** 路由规则对某个模型的引用：主模型（primary）或故障转移（fallback） */
export interface CatalogReference {
  id: number
  agent_type: string
  task_type: string
  priority: number
  is_enabled: boolean
  role: 'primary' | 'fallback'
}

export interface CatalogReferencesResponse {
  model_id: string
  provider: string
  routes: CatalogReference[]
  total: number
}

/** 迁移到替代模型；evidence（评测依据）与 override_reason（跳过评测门禁的原因）至少填一个 */
export interface CatalogMigrateRequest {
  target_model: string
  target_provider: string
  failover_model?: string | null
  failover_provider?: string | null
  evidence?: string | null
  override_reason?: string | null
  reason?: string | null
}

export interface CatalogMigrateResponse {
  model_id: string
  target_model: string
  updated_rules: number
  updated_fallbacks: number
  audit_id: string
  /** 提交后刷新网关路由与模型目录缓存是否成功；false 时路由缓存最长 60 秒后自动刷新 */
  cache_refreshed?: boolean
}

/** 模型目录变更记录（审计日志里 resource_type=model_catalog 的行） */
export interface CatalogChange {
  id: string
  created_at: string
  action: string
  resource_id: string | null
  resource_name: string | null
  user_id: string
  status: string
  old_value: Record<string, unknown> | null
  new_value: Record<string, unknown> | null
}

export interface ProbeEmbeddingDimensionRequest {
  provider: string
  model: string
  base_url?: string | null
  api_key?: string | null
}

export interface ProbeEmbeddingDimensionResponse {
  provider: string
  model: string
  dimension: number
  latency_ms: number
}

// ==================== API 方法 ====================

/**
 * 获取可用模型目录
 *
 * @param params - 可选过滤参数（provider / tag）
 * @returns 已启用的模型列表
 */
export function fetchModelCatalog(params?: FetchModelCatalogParams): Promise<CatalogModel[]> {
  return apiClient.get<CatalogModel[]>('/model-config/catalog', { params })
}

/** 新增模型目录条目 */
export function createCatalogModel(data: CreateCatalogModelRequest): Promise<CatalogModel> {
  return apiClient.post<CatalogModel>('/model-config/catalog', data)
}

/** 更新模型目录条目；model_id 可能含 `/`，由后端 path converter 接收 */
export function updateCatalogModel(
  modelId: string,
  data: UpdateCatalogModelRequest,
  provider?: string,
): Promise<CatalogModel> {
  const url = `/model-config/catalog/${encodeURIComponent(modelId)}`
  if (!provider) {
    return apiClient.put<CatalogModel>(url, data)
  }
  return apiClient.put<CatalogModel>(url, data, { params: { provider } })
}

/** 启用 / 停用模型目录条目（后端翻转 is_enabled）。 */
export function toggleCatalogModel(modelId: string, provider?: string): Promise<CatalogModel> {
  const url = `/model-config/catalog/${encodeURIComponent(modelId)}/toggle`
  return provider ? apiClient.post<CatalogModel>(url, undefined, { params: { provider } }) : apiClient.post<CatalogModel>(url)
}

/** 哪些路由规则的主模型或故障转移引用了该模型。仅管理员可读。 */
export function fetchCatalogReferences(modelId: string, provider?: string): Promise<CatalogReferencesResponse> {
  const url = `/model-config/catalog/${encodeURIComponent(modelId)}/references`
  return provider ? apiClient.get<CatalogReferencesResponse>(url, { params: { provider } }) : apiClient.get<CatalogReferencesResponse>(url)
}

/** 迁移到替代模型：改路由规则的主模型与故障转移，源模型置为已下线，写变更记录。 */
export function migrateCatalogModel(
  modelId: string,
  provider: string,
  body: CatalogMigrateRequest,
): Promise<CatalogMigrateResponse> {
  return apiClient.post<CatalogMigrateResponse>(
    `/model-config/catalog/${encodeURIComponent(modelId)}/migrate`,
    body,
    { params: { provider } },
  )
}

/** 模型目录变更记录，按时间倒序。 */
export function fetchCatalogChanges(params?: { limit?: number; offset?: number }): Promise<CatalogChange[]> {
  return apiClient.get<CatalogChange[]>('/model-config/catalog/changes', { params })
}

/** 探测 Embedding 模型输出维度。 */
export function probeEmbeddingDimension(
  data: ProbeEmbeddingDimensionRequest,
): Promise<ProbeEmbeddingDimensionResponse> {
  return apiClient.post<ProbeEmbeddingDimensionResponse>(
    '/model-config/catalog/probe-embedding-dimension',
    data,
  )
}

// ==================== Routing Rules（Agent 模型路由 / T12）====================
// 对接后端 /model-config/routing-rules（admin CRUD）。让核心/内置 agent 的
// agent_type → model 路由可在「模型配置」页热编辑；写入后端刷新 ModelGateway 缓存
// （实际生效需 ENABLE_MODEL_GATEWAY=true）。与上方 catalog 同属 /model-config/*，
// 故归并于本文件，避免再开第三个 model-config 客户端文件。

/** 路由规则（对应后端 RoutingRuleResponse ORM） */
export interface RoutingRule {
  id: number
  /** 模型类型: chat/image/video/embedding/rerank（页面按此分组） */
  model_type: string
  agent_type: string
  task_type: string
  provider: string
  model: string
  fallback_chain: Array<Record<string, unknown>> | null
  temperature: number | null
  max_tokens: number | null
  timeout: number | null
  priority: number
  is_enabled: boolean
  /** 协议/媒体扩展: {protocol, model_version, ...}（生图 VOD 接入） */
  extra_config: Record<string, unknown> | null
  description: string | null
  created_at: string | null
  updated_at: string | null
}

/** 新增路由规则请求（对应后端 RoutingRuleCreate） */
export interface RoutingRuleCreate {
  /** 模型类型；留空则后端按 agent_type 自动派生（image/video/embedding→同名，其余→chat） */
  model_type?: string
  agent_type: string
  task_type?: string
  provider: string
  model: string
  fallback_chain?: Array<Record<string, unknown>> | null
  temperature?: number | null
  max_tokens?: number | null
  timeout?: number | null
  priority?: number
  is_enabled?: boolean
  description?: string | null
  /** 协议/媒体扩展: {protocol, model_version, ...}（生图 VOD 接入；vod-task 必带 model_version） */
  extra_config?: Record<string, unknown> | null
}

/** 更新路由规则请求（所有字段可选，对应后端 RoutingRuleUpdate） */
export type RoutingRuleUpdate = Partial<RoutingRuleCreate>

const ROUTING_RULES_BASE = '/model-config/routing-rules'

/** 获取路由规则（按 priority 降序，可按 model_type 分组筛选）。仅管理员可读。 */
export function fetchRoutingRules(modelType?: string): Promise<RoutingRule[]> {
  const params = modelType ? { model_type: modelType } : undefined
  return apiClient.get<RoutingRule[]>(ROUTING_RULES_BASE, { params })
}

/** 新增路由规则。 */
export function createRoutingRule(data: RoutingRuleCreate): Promise<RoutingRule> {
  return apiClient.post<RoutingRule>(ROUTING_RULES_BASE, data)
}

/** 更新路由规则（如切换某 agent_type 的 model）。 */
export function updateRoutingRule(id: number, data: RoutingRuleUpdate): Promise<RoutingRule> {
  return apiClient.put<RoutingRule>(`${ROUTING_RULES_BASE}/${id}`, data)
}

/** 删除路由规则。 */
export function deleteRoutingRule(id: number): Promise<void> {
  return apiClient.delete<void>(`${ROUTING_RULES_BASE}/${id}`)
}
